/** @jsx jsx */
import {
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  GlobalOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { API } from "@app/services/api/api";
import { COLORS } from "@app/styles/consts";
import { getTranslationKey } from "@app/translations/translations.helpers";
import { DataTestIds } from "@app/utils/dataTestIds";
import { unpackErrorMessage } from "@app/utils/error";
import { MessageBar } from "@ea/shared_components/MessageBar";
import { DatasourceType } from "@ea/shared_types/next/ea.enums";
import { DataSourceMetadata, TaskScript } from "@ea/shared_types/next/ea.types";
import { EntityType, MESSAGE_TYPE } from "@ea/shared_types/types";
import { css, jsx } from "@emotion/core";
import styled from "@emotion/styled";
import { Button, Spin, Tooltip } from "antd";
import * as React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage } from "react-intl";
import { ConnectedProps, connect } from "react-redux";
import { toast } from "react-toastify";
import { variableActions } from "../variables/variables.actions";
import AssignGlobalExcelContainer from "./AssignGlobalExcel.container";
import { scriptsActions } from "./scripts.actions";
interface IDataSourceContainerProps {
  parentId: number;
  parentType: EntityType;
  viewOnly?: boolean;
  onUpload?: () => void;
  projectId?: number;
  script?: TaskScript;
}

interface IDataSourceContainerState {
  files: DataSourceMetadata[];
  loading: boolean;
  error: string | undefined;
}

const Container = styled.div({
  display: "flex",
  width: "100%",
  flexDirection: "column",
});

const LoadedFilesContainer = styled.div({
  display: "flex",
  flexDirecion: "column",
});

const LoadedFile = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "5px",
});

const DropZoneDiv = styled.div(
  {
    borderRadius: "4px",
    background: "#f4f4f4",
    border: "2px dashed #d9d9d9",
    transition: "border-color .3s",
    width: "400px",
    height: "150px",
    textAlign: "center",
    padding: "16px 0",
    cursor: "pointer",
    "&:hover": {
      background: "#dbdbdb",
      border: "2px dashed #2b2b2b",
    },
  },
  ({ isDragActive }: { isDragActive: boolean }) =>
    isDragActive ? { border: "2px dashed green" } : {},
);

const spinner = css({
  width: "400px",
  height: "150px",
});

const documentIcon = css({
  fontSize: 30,
  margin: "10px",
  color: COLORS.DARK_GREEN,
  cursor: "pointer",
  ":hover": {
    textShadow: "0px 0px 5px lightgreen",
  },
});

class DataSourceContainer extends React.Component<IConnectProps, IDataSourceContainerState> {
  formRef: any;
  dropzoneRef: any;

  state: IDataSourceContainerState = {
    files: [],
    loading: false,
    error: undefined,
  };

  async componentDidMount() {
    this.setState({
      loading: true,
    });
    const files = await this.getFiles();
    this.setState({
      files,
      loading: false,
    });
  }

  async getFiles() {
    let files: DataSourceMetadata[] = [];
    try {
      if (this.props.script?.datasourceId) {
        files = await API.getDataSources({
          filter: { where: { id: this.props.script?.datasourceId } },
        });
      }
    } catch (err) {
      this.setState({
        loading: false,
        error: getTranslationKey("messages", "error", "fileGet"),
      });
      console.error(unpackErrorMessage(err));
      return [];
    }
    return files;
  }

  onDrop = async (files: File[]) => {
    const { parentId, onUpload, loadVariables, loadScript } = this.props;
    let createdFiles: DataSourceMetadata[] = [];
    this.setState({
      loading: true,
    });
    try {
      createdFiles = await API.uploadFile({
        files,
        scriptId: parentId,
      } as any);
    } catch (err) {
      this.setState({
        loading: false,
        error: unpackErrorMessage(err) || `${getTranslationKey("messages", "error", "fileUpload")}`,
      });
      console.error(unpackErrorMessage(err));
      toast.error(<FormattedMessage id={getTranslationKey("messages", "error", "fileUpload")} />);
      return;
    }
    toast.success(<FormattedMessage id={getTranslationKey("messages", "success", "fileUpload")} />);
    loadVariables(parentId);
    loadScript({ id: parentId });

    this.setState({
      files: createdFiles,
      loading: false,
    });
    onUpload?.();
  };

  onReject = () => {
    toast.warn(<FormattedMessage id={getTranslationKey("messages", "error", "fileUpload")} />);
  };

  onDelete = async (datasourceId: number) => {
    this.setState({
      loading: true,
    });
    const { parentId, loadVariables } = this.props;
    let files: DataSourceMetadata[] = [];
    try {
      files = await API.deleteDataSource({ id: datasourceId }).then(() => this.getFiles());
    } catch (err) {
      this.setState({
        loading: false,
        error: unpackErrorMessage(err),
      });
      console.error(unpackErrorMessage(err));
      toast.error(<FormattedMessage id={getTranslationKey("messages", "error", "fileDownload")} />);
      return;
    }
    toast.success(
      <FormattedMessage id={getTranslationKey("messages", "success", "fileRemoved")} />,
    );
    loadVariables(parentId);
    this.setState({
      files: files,
      loading: false,
    });
  };

  onUnassignClick = async () => {
    const { loadVariables, parentId } = this.props;
    try {
      await API.unassignDatasourceFromScript({ scriptId: parentId });
      toast.success(
        <FormattedMessage id={getTranslationKey("messages", "success", "unassignedGlobalExcel")} />,
      );
      this.setState({
        files: [],
      });

      await loadVariables(parentId);
    } catch (e) {
      console.error(e);
      toast.error(
        <FormattedMessage id={getTranslationKey("messages", "error", "unassignedGlobalExcel")} />,
      );
    }
  };

  onGlobalExcelAssign = (file: DataSourceMetadata) => {
    const { loadVariables, loadScript, parentId } = this.props;
    loadVariables(parentId);
    loadScript({ id: parentId });
    this.setState({ files: [file] });
  };

  onDownload = async (file: DataSourceMetadata) => {
    const { name, originalFilename, id } = file;
    try {
      API.downloadFile({ id, fileName: name, originalFilename });
    } catch (err) {
      console.error(unpackErrorMessage(err));
      this.setState({
        error: getTranslationKey("messages", "error", "fileDownload"),
      });
      return;
    }
  };

  renderDropzone = () => (
    <Dropzone
      ref={(element) => {
        this.dropzoneRef = element;
      }}
      multiple={false}
      onDropAccepted={this.onDrop}
      onDropRejected={this.onReject}
      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <DropZoneDiv {...(getRootProps() as any)} isDragActive={isDragActive}>
          <input {...(getInputProps() as any)} data-testid={DataTestIds.DROPZONE} />
          <UploadOutlined style={{ fontSize: 50 }} />
          <p>
            <FormattedMessage id={getTranslationKey("messages", "info", "dropZone")} />
          </p>
        </DropZoneDiv>
      )}
    </Dropzone>
  );

  renderDataSourceList = () =>
    this.state.files.map((file, index) => (
      <LoadedFile key={index}>
        {file.type === DatasourceType.GLOBAL && (
          <Tooltip
            title={<FormattedMessage id={getTranslationKey("globalExcel", "tooltip", "icon")} />}
          >
            <GlobalOutlined />
          </Tooltip>
        )}
        <Tooltip title={<FormattedMessage id={getTranslationKey("button", "download")} />}>
          <FileExcelOutlined css={documentIcon} onClick={() => this.onDownload(file)} />
        </Tooltip>
        <h3 style={{ margin: 0 }}>{file.originalFilename}</h3>
        <Tooltip title={<FormattedMessage id={getTranslationKey("button", "download")} />}>
          <Button
            icon={<DownloadOutlined />}
            style={{ marginLeft: "20px" }}
            onClick={() => this.onDownload(file)}
          />
        </Tooltip>
        {file.type === DatasourceType.LOCAL && (
          <Tooltip title={<FormattedMessage id={getTranslationKey("button", "reload")} />}>
            <Button
              icon={<ReloadOutlined />}
              title="Reload"
              style={{ marginLeft: "10px" }}
              onClick={() => {
                this.dropzoneRef.open();
              }}
            />
          </Tooltip>
        )}
        {!this.props.viewOnly && file.type === DatasourceType.LOCAL && (
          <Tooltip title={<FormattedMessage id={getTranslationKey("button", "delete")} />}>
            <Button
              icon={<DeleteOutlined />}
              danger
              style={{ marginLeft: "10px" }}
              onClick={() => this.onDelete(file.id)}
            />
          </Tooltip>
        )}
        {!this.props.viewOnly && file.type === DatasourceType.GLOBAL && (
          <Tooltip title={<FormattedMessage id={getTranslationKey("button", "unassign")} />}>
            <Button
              icon={<MinusCircleOutlined />}
              danger
              style={{ marginLeft: "10px" }}
              onClick={this.onUnassignClick}
            />
          </Tooltip>
        )}
      </LoadedFile>
    ));

  render() {
    const { loading, error, files } = this.state;
    const { viewOnly } = this.props;
    if (loading) {
      return (
        <Container>
          <Spin css={spinner} style={{ marginTop: "50px", marginRight: "200px" }} />
        </Container>
      );
    }
    return (
      <Container>
        {error && <MessageBar messages={[{ text: error, type: MESSAGE_TYPE.ERROR }]} />}
        <div
          style={{
            display: files.length === 0 && !viewOnly ? "initial" : "none",
          }}
        >
          {this.renderDropzone()}
        </div>

        {(files.length !== 0 || viewOnly) && (
          <LoadedFilesContainer>
            {files.length ? (
              this.renderDataSourceList()
            ) : (
              <p>
                <FormattedMessage id={getTranslationKey("messages", "info", "noDataSource")} />
              </p>
            )}
          </LoadedFilesContainer>
        )}
        {!this.props.viewOnly && files.length === 0 && (
          <AssignGlobalExcelContainer
            projectId={this.props.projectId}
            scriptId={this.props.parentId}
            onGlobalExcelAssign={this.onGlobalExcelAssign}
          />
        )}
      </Container>
    );
  }
}

const connectCreator = connect((state, props: IDataSourceContainerProps) => props, {
  loadVariables: variableActions.loadDataSource,
  loadScript: scriptsActions.loadSingle,
});

type IConnectProps = ConnectedProps<typeof connectCreator>;

export default connectCreator(DataSourceContainer);
